import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Snackbar, Alert, Box, Typography, Switch, Skeleton } from '@mui/material';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import DateChecklistExpand from './DateChecklistExpand';
import ExpandableSection from './ExpandableSection';
import { checklistService, datedChecklistService } from '../services/api';
import { ChecklistItem, ChecklistType, DatedChecklistMetadata } from '../types/api';

const pasoChecklistConfigs = [
  { type: 'escalated', title: 'Escalated 📈' },
  { type: 'wsfj_extremely_urgent', title: 'Do ASAP' },
  { type: 'want_to_do', title: 'Psyche-Soma Wants' },
  { type: 'wsfj_more_value_sooner', title: 'More Value if Done Soon' },
  { type: 'wsfj_no_rush', title: 'No Rush' },
] as const satisfies ReadonlyArray<{ type: ChecklistType; title: string }>;

const visibilityDebugEnabled = process.env.NODE_ENV !== 'production';

interface ChecklistState {
  date: string;
  title: string;
  dayOfWeek: string;
  items: ChecklistItem[];
  itemCount?: number; // Count of items (loaded first, before items)
  expanded: boolean;
  reviewed: boolean;
  reviewedAt?: string;
}

interface DateBasedChecklistsProps {
  filterMode: 'future' | 'past';
  title?: string;
  expandAndScrollToDate?: string | null;
  onDateExpanded?: () => void;
}

const DateBasedChecklists: React.FC<DateBasedChecklistsProps> = ({ 
  filterMode,
  title = filterMode === 'future' ? 'Get Ahead?' : 'Past Days',
  expandAndScrollToDate,
  onDateExpanded
}) => {
  const [checklists, setChecklists] = useState<Record<string, ChecklistState>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [isDragInProgress, setIsDragInProgress] = useState(false);
  const [toggleAll, setToggleAll] = useState(false);
  const [showOnlyUnreviewed, setShowOnlyUnreviewed] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const expandedStatesRef = useRef<Record<string, boolean>>({});
  const lastActivityRef = useRef<number>(Date.now());
  const dateRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const isActivelyUsing = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    return timeSinceActivity < 30000; // Active within last 30 seconds
  }, []);

  // Update ref whenever checklists change
  useEffect(() => {
    const states: Record<string, boolean> = {};
    Object.keys(checklists).forEach(date => {
      states[date] = checklists[date].expanded;
    });
    expandedStatesRef.current = states;
  }, [checklists]);

  // Track activity on any user interaction
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  // Get current date in user's local timezone (YYYY-MM-DD format)
  const getCurrentDate = useCallback(() => {
    return new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
  }, []);

  // Check if a date is in the past
  const isPastDate = useCallback((dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00'); // Parse as local date
    const today = new Date(getCurrentDate() + 'T00:00:00');
    return date < today;
  }, [getCurrentDate]);

  // Filter checklists based on filterMode
  const filterChecklists = useCallback((allChecklists: DatedChecklistMetadata[]) => {
    return allChecklists.filter(checklist => {
      const isPast = isPastDate(checklist.date);
      return filterMode === 'past' ? isPast : !isPast;
    });
  }, [filterMode, isPastDate]);

  // Load all checklists from backend
  const loadChecklists = useCallback(async (preserveExpandedForDates?: string[]) => {
    // Use ref to get current expanded states (avoids stale closure)
    const currentExpandedStates = expandedStatesRef.current;

    try {
      // Step 1: Get all date metadata (fast - shows dates immediately)
      const response = await datedChecklistService.getAllChecklists();
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load checklists');
      }

      const filteredChecklists = filterChecklists(response.data.checklists);
      
      // Step 2: Create initial checklist state with metadata only
      const newChecklists: Record<string, ChecklistState> = {};
      // Track dates that are expanded so we can re-fetch their items after reset
      const expandedDatesToRefetch: string[] = [];
      
      for (const metadata of filteredChecklists) {
        const shouldPreserveExpanded = preserveExpandedForDates?.includes(metadata.date);
        const isExpanded = shouldPreserveExpanded ? (currentExpandedStates[metadata.date] ?? false) : false;
        newChecklists[metadata.date] = {
          date: metadata.date,
          title: metadata.title,
          dayOfWeek: metadata.dayOfWeek,
          items: [], // Empty initially
          itemCount: undefined, // Will be loaded next
          expanded: isExpanded,
          reviewed: metadata.reviewed || false,
          reviewedAt: metadata.reviewedAt,
        };
        // If the checklist was expanded, we must re-fetch its items since we reset them to []
        // above. Without this, DateChecklistExpand will show an infinite loading spinner.
        if (isExpanded) {
          expandedDatesToRefetch.push(metadata.date);
        }
      }

      // Show dates immediately
      setChecklists(newChecklists);
      setInitialLoading(false); // Loading complete, dates are visible

      // Fetch item counts in parallel for all dates
      const countPromises = filteredChecklists.map(metadata =>
        datedChecklistService.getChecklistItemCount(metadata.date)
          .then(countResponse => ({
            date: metadata.date,
            count: countResponse.success && countResponse.count !== undefined ? countResponse.count : 0,
          }))
          .catch(() => ({
            date: metadata.date,
            count: 0,
          }))
      );

      const counts = await Promise.all(countPromises);

      // Update checklists with counts
      setChecklists(prev => {
        const updated = { ...prev };
        counts.forEach(({ date, count }) => {
          if (updated[date]) {
            updated[date] = { ...updated[date], itemCount: count };
          }
        });
        return updated;
      });

      // Re-fetch items for any checklists that were expanded when the refresh fired.
      // loadChecklists resets items:[] for every date, so without this step an expanded
      // checklist would be left with expanded=true / items=[] / itemCount>0 — the exact
      // condition that makes DateChecklistExpand show a loading spinner indefinitely.
      if (expandedDatesToRefetch.length > 0) {
        const itemResults = await Promise.all(
          expandedDatesToRefetch.map(date =>
            datedChecklistService.getChecklist(date)
              .then(itemsResponse => ({ date, itemsResponse }))
              .catch(() => null)
          )
        );

        setChecklists(prev => {
          const updated = { ...prev };
          itemResults.forEach(result => {
            if (result && result.itemsResponse.success && result.itemsResponse.data && updated[result.date]) {
              updated[result.date] = {
                ...updated[result.date],
                items: result.itemsResponse.data.items,
                itemCount: result.itemsResponse.data.items.length,
              };
            }
          });
          return updated;
        });
      }

    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to load checklists',
        severity: 'error',
      });
    }
  }, [filterChecklists]);

  // Handle expand/collapse changes - loads items on every expand or collapse
  const handleExpandChange = useCallback(async (date: string, expanded: boolean) => {
    // Update expanded state immediately
    setChecklists(prev => ({
      ...prev,
      [date]: { ...prev[date], expanded },
    }));

    // Fetch fresh items on every expand or collapse
    try {
      const itemsResponse = await datedChecklistService.getChecklist(date);
      if (itemsResponse.success && itemsResponse.data) {
        setChecklists(current => ({
          ...current,
          [date]: {
            ...current[date],
            dayOfWeek: itemsResponse.data!.dayOfWeek || current[date].dayOfWeek,
            items: itemsResponse.data!.items,
            itemCount: itemsResponse.data!.items.length,
          },
        }));
      }
    } catch (error) {
      console.error(`Failed to load items for ${date}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to load items for ${date}`,
        severity: 'error',
      });
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  // Handle expanding and scrolling to a specific date after creation
  useEffect(() => {
    if (expandAndScrollToDate && checklists[expandAndScrollToDate]) {
      // Expand the target date using handleExpandChange to ensure items are loaded
      handleExpandChange(expandAndScrollToDate, true);

      // Scroll to the element after a short delay to ensure rendering is complete
      setTimeout(() => {
        const element = dateRefs.current[expandAndScrollToDate];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // Notify parent that expansion is complete
        if (onDateExpanded) {
          onDateExpanded();
        }
      }, 100);
    }
  }, [expandAndScrollToDate, checklists, onDateExpanded, handleExpandChange]);

  // Poll every 5 minutes to check for date changes
  useEffect(() => {
    pollIntervalRef.current = setInterval(() => {
      // Use functional update to get current checklists at time of execution
      setChecklists(currentChecklists => {
        const visibleDates = Object.keys(currentChecklists);
        
        // For upcoming mode, check activity before refreshing
        if (filterMode === 'future' && isActivelyUsing()) {
          console.log('User is active - skipping scheduled poll refresh');
          return currentChecklists; // No state change
        }
        
        loadChecklists(visibleDates);
        return currentChecklists; // No state change, just side effect
      });
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadChecklists, filterMode, isActivelyUsing]);

  // Schedule refresh at midnight to update date categories
  useEffect(() => {
    const scheduleNextMidnightRefresh = (): NodeJS.Timeout => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const msUntilMidnight = tomorrow.getTime() - now.getTime();

      // Refresh shortly after midnight (add 1 second buffer)
      return setTimeout(() => {
        console.log('Midnight detected - checking for date category updates for', filterMode);
        
        if (filterMode === 'past') {
          // Past mode: Safe to auto-refresh - just adds yesterday to the list
          // Preserve expanded states to maintain user context
          // Get current visible dates at time of execution
          setChecklists(currentChecklists => {
            const visibleDates = Object.keys(currentChecklists);
            loadChecklists(visibleDates);
            return currentChecklists; // No state change, just side effect
          });
        } else {
          // Upcoming mode: Be careful - today will disappear from view
          if (!isActivelyUsing()) {
            // User is idle - safe to refresh
            setChecklists(currentChecklists => {
              const visibleDates = Object.keys(currentChecklists);
              loadChecklists(visibleDates);
              return currentChecklists;
            });
          } else {
            // User is active - defer refresh, will be caught by next polling cycle
            console.log('User is active - deferring midnight refresh until user is idle');
          }
        }
        
        // Schedule next day's refresh
        const nextTimer = scheduleNextMidnightRefresh();
        return () => clearTimeout(nextTimer);
      }, msUntilMidnight + 1000);
    };

    const timer = scheduleNextMidnightRefresh();
    
    return () => clearTimeout(timer);
  }, [loadChecklists, filterMode, isActivelyUsing]);

  const handleToggleAll = (checked: boolean) => {
    setToggleAll(checked);
    
    if (checked) {
      // When expanding all, use handleExpandChange for each date to properly load items
      Object.keys(checklists).forEach(date => {
        handleExpandChange(date, true);
      });
    } else {
      // When collapsing all, just update the expanded state
      setChecklists(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(date => {
          updated[date].expanded = false;
        });
        return updated;
      });
    }
  };

  const handleAddItem = async (
    date: string,
    text: string,
    options?: { successMessage?: string | null }
  ) => {
    try {
      const response = await datedChecklistService.createItem(date, { text });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create item');
      }

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: [...prev[date].items, response.data!],
          itemCount: (prev[date].itemCount || 0) + 1, // Increment count
        },
      }));

      if (options?.successMessage !== null) {
        setSnackbar({
          open: true,
          message: options?.successMessage || 'Item added successfully',
          severity: 'success',
        });
      }

      return true;
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to add item',
        severity: 'error',
      });
    }

    return false;
  };

  const formatChecklistTitle = (dateStr: string) => {
    const dateObj = new Date(dateStr + 'T00:00:00');
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleMoveItemToDate = async (
    itemId: string,
    currentDate: string,
    targetDate: string,
    newText: string,
    options?: { tags?: ChecklistItem['tags'] | null }
  ) => {
    try {
      const currentItem = checklists[currentDate]?.items.find(item => item.id === itemId);
      if (!currentItem) {
        throw new Error('Item not found');
      }

      const existingTargetChecklist = checklists[targetDate];
      if (!existingTargetChecklist) {
        const checklistResponse = await datedChecklistService.createChecklist({
          date: targetDate,
          title: formatChecklistTitle(targetDate),
        });
        if (!checklistResponse.success) {
          if (checklistResponse.code !== 'CHECKLIST_ALREADY_EXISTS') {
            throw new Error(checklistResponse.error || 'Failed to create target checklist');
          }
        }
      }

      const response = await datedChecklistService.createItem(targetDate, {
        text: newText,
        ...(options?.tags !== undefined ? { tags: options.tags ?? null } : {}),
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to move item');
      }

      const movedItem = response.data;
      if (currentItem.customColor) {
        const colorResponse = await datedChecklistService.updateItemColor(targetDate, movedItem.id, currentItem.customColor);
        if (!colorResponse.success) {
          throw new Error(colorResponse.error || 'Failed to transfer item color');
        }
      }

      const deleteResponse = await datedChecklistService.deleteItem(currentDate, itemId);
      if (!deleteResponse.success) {
        throw new Error(deleteResponse.error || 'Failed to remove original item');
      }

      setChecklists(prev => {
        const updated = { ...prev };
        const sourceChecklist = updated[currentDate];
        const targetChecklist = updated[targetDate] || {
          date: targetDate,
          title: formatChecklistTitle(targetDate),
          dayOfWeek: '',
          items: [],
          itemCount: 0,
          expanded: false,
          reviewed: false,
        };

        updated[currentDate] = {
          ...sourceChecklist,
          items: sourceChecklist?.items.filter(item => item.id !== itemId) || [],
          itemCount: Math.max(0, (sourceChecklist?.itemCount || 1) - 1),
        };

        updated[targetDate] = {
          ...targetChecklist,
          items: [...(targetChecklist.items || []), movedItem],
          itemCount: (targetChecklist.itemCount || 0) + 1,
          expanded: true,
        };

        return updated;
      });

      await handleExpandChange(targetDate, true);

      // After the target accordion is expanded, bring it into view.
      setTimeout(() => {
        const targetElement = dateRefs.current[targetDate];
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      const destinationTitle = checklists[targetDate]?.title || formatChecklistTitle(targetDate);
      setSnackbar({
        open: true,
        message: `Item moved to ${destinationTitle}`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to move item',
        severity: 'error',
      });
    }
  };

  const handleDeleteItem = async (date: string, itemId: string) => {
    try {
      const response = await datedChecklistService.deleteItem(date, itemId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete item');
      }

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: prev[date].items.filter(item => item.id !== itemId),
          itemCount: Math.max(0, (prev[date].itemCount || 1) - 1), // Decrement count
        },
      }));

      setSnackbar({
        open: true,
        message: 'Item deleted',
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to delete item',
        severity: 'error',
      });
    }
  };

  const handleEditItem = async (
    date: string,
    itemId: string,
    newText: string,
    recurringMetadataId?: string,
    shouldReload?: boolean,
    options?: { tags?: ChecklistItem['tags'] | null }
  ) => {
    try {
      // If shouldReload is true, this was a recurring item update - reload the specific date's items
      if (shouldReload) {
        const itemsResponse = await datedChecklistService.getChecklist(date);
        if (itemsResponse.success && itemsResponse.data) {
          setChecklists(prev => ({
            ...prev,
            [date]: {
              ...prev[date],
              items: itemsResponse.data!.items,
              itemCount: itemsResponse.data!.items.length,
            },
          }));
        }
        setSnackbar({
          open: true,
          message: 'Item updated',
          severity: 'success',
        });
        return;
      }

      // Update the item text and/or recurring link
      let response: any;
      if (recurringMetadataId !== undefined && recurringMetadataId !== '') {
        // Converting to recurring - use the convert endpoint which handles both text update and linkage creation
        response = await datedChecklistService.convertItemToRecurring(date, itemId, recurringMetadataId);
      } else if (recurringMetadataId === '') {
        // Converting to non-recurring - use the convert-to-non-recurring endpoint
        // Pass the new text so it's set atomically in the same transaction
        response = await datedChecklistService.convertItemToNonRecurring(date, itemId, newText);
      } else {
        // Simple text update without changing recurring status
        response = await datedChecklistService.updateItem(date, itemId, {
          text: newText,
          ...(options ? { tags: options.tags ?? null } : {}),
        });
      }
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update item');
      }

      const updatedItem = response.data;

      // Update local state
      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: prev[date].items.map(item =>
            item.id === itemId ? updatedItem : item
          ),
        },
      }));

      setSnackbar({
        open: true,
        message: 'Item updated',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update item',
        severity: 'error',
      });
    }
  };

  const handleSetItemHidden = async (date: string, itemId: string, isHidden: boolean) => {
    const currentItems = checklists[date]?.items ?? [];
    const currentItem = currentItems.find(item => item.id === itemId);
    const optimisticItem = currentItem ? { ...currentItem, isHidden } : undefined;

    const buildUpdatedItems = (items: ChecklistItem[], serverItem?: ChecklistItem) => {
      const nextItem = serverItem ?? optimisticItem;
      if (!nextItem) {
        return items;
      }

      if (isHidden) {
        const currentIndex = items.findIndex(item => item.id === itemId);
        if (currentIndex >= 0) {
          const reorderedItems = [...items.filter(item => item.id !== itemId), items[currentIndex]];
          return reorderedItems.map(item => (item.id === itemId ? { ...item, ...nextItem, isHidden } : item));
        }
      }

      return items.map(item => (item.id === itemId ? { ...item, ...nextItem, isHidden } : item));
    };

    const previousItems = currentItems;
    setChecklists(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        items: buildUpdatedItems(prev[date]?.items ?? currentItems),
      },
    }));

    try {
      if (visibilityDebugEnabled) {
        console.info('[VISIBILITY] request', {
          date,
          itemId,
          requestedHidden: isHidden,
          currentHidden: currentItem?.isHidden === true,
        });
      }

      const response = await datedChecklistService.updateItem(date, itemId, { isHidden });
      if (visibilityDebugEnabled) {
        console.info('[VISIBILITY] updateItem response', {
          date,
          itemId,
          requestedHidden: isHidden,
          success: response.success,
          error: response.error,
          returnedHidden: response.data?.isHidden === true,
        });
      }
      if (!response.success) {
        throw new Error(response.error || 'Failed to update item visibility');
      }

      const updatedItem = {
        ...(response.data ?? optimisticItem ?? {}),
        isHidden,
      } as ChecklistItem;

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: buildUpdatedItems(prev[date]?.items ?? currentItems, updatedItem),
        },
      }));

      if (isHidden) {
        const currentIndex = previousItems.findIndex(item => item.id === itemId);
        if (currentIndex >= 0) {
          const reorderedItems = [...previousItems.filter(item => item.id !== itemId), previousItems[currentIndex]];
          const previousId = reorderedItems.length > 1 ? reorderedItems[reorderedItems.length - 2].id : '';

          const reorderResponse = await datedChecklistService.reorderItem(date, itemId, { previousId });
          if (!reorderResponse.success) {
            throw new Error(reorderResponse.error || 'Failed to reorder hidden item');
          }
        }
      }

      let refreshedChecklist = await datedChecklistService.getChecklist(date);
      if (!refreshedChecklist.success || !refreshedChecklist.data) {
        throw new Error(refreshedChecklist.error || 'Failed to refresh checklist after visibility update');
      }

      const persistedItem = refreshedChecklist.data.items.find(item => item.id === itemId);
      if (!persistedItem) {
        throw new Error('Updated item not found after visibility update');
      }

      const persistedHidden = persistedItem.isHidden === true;
      if (visibilityDebugEnabled) {
        console.info('[VISIBILITY] persisted check', {
          date,
          itemId,
          requestedHidden: isHidden,
          persistedHidden,
        });
      }
      if (persistedHidden !== isHidden) {
        if (visibilityDebugEnabled) {
          console.warn('[VISIBILITY] mismatch after update, retrying', {
            date,
            itemId,
            requestedHidden: isHidden,
            persistedHidden,
          });
        }
        const retryResponse = await datedChecklistService.updateItem(date, itemId, { isHidden });
        if (visibilityDebugEnabled) {
          console.info('[VISIBILITY] retry updateItem response', {
            date,
            itemId,
            requestedHidden: isHidden,
            success: retryResponse.success,
            error: retryResponse.error,
            returnedHidden: retryResponse.data?.isHidden === true,
          });
        }
        if (!retryResponse.success) {
          throw new Error(retryResponse.error || 'Visibility update did not persist (retry failed)');
        }

        refreshedChecklist = await datedChecklistService.getChecklist(date);
        if (!refreshedChecklist.success || !refreshedChecklist.data) {
          throw new Error(refreshedChecklist.error || 'Failed to refresh checklist after visibility retry');
        }

        const retryPersistedItem = refreshedChecklist.data.items.find(item => item.id === itemId);
        const retryPersistedHidden = retryPersistedItem?.isHidden === true;
        if (visibilityDebugEnabled) {
          console.info('[VISIBILITY] retry persisted check', {
            date,
            itemId,
            requestedHidden: isHidden,
            retryPersistedHidden,
            foundItem: Boolean(retryPersistedItem),
          });
        }
        if (!retryPersistedItem || retryPersistedHidden !== isHidden) {
          throw new Error('Visibility update did not persist on backend; please retry');
        }
      }

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: refreshedChecklist.data!.items,
          itemCount: refreshedChecklist.data!.items.length,
        },
      }));
    } catch (error) {
      if (visibilityDebugEnabled) {
        console.error('[VISIBILITY] failed', {
          date,
          itemId,
          requestedHidden: isHidden,
          error: error instanceof Error ? error.message : String(error),
        });
      }
      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: previousItems,
        },
      }));
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update item visibility',
        severity: 'error',
      });
    }
  };

  const handleAddItemToPaso = async (date: string, itemId: string, targetChecklistType: ChecklistType) => {
    try {
      const sourceItem = checklists[date]?.items.find(item => item.id === itemId);
      if (!sourceItem) {
        throw new Error('Item not found');
      }
      if (sourceItem.recurringMetadataId) {
        throw new Error('Recurring items cannot be added to PASOS from calendar');
      }

      const createResponse = await checklistService.createItem(targetChecklistType, {
        text: sourceItem.text,
        ...(sourceItem.tags !== undefined ? { tags: sourceItem.tags ?? null } : {}),
      });
      if (!createResponse.success || !createResponse.data) {
        throw new Error(createResponse.error || 'Failed to add item to PASOS');
      }

      if (sourceItem.customColor) {
        const colorResponse = await checklistService.updateItemColor(targetChecklistType, createResponse.data.id, sourceItem.customColor);
        if (!colorResponse.success) {
          throw new Error(colorResponse.error || 'Failed to transfer item color');
        }
      }

      const deleteResponse = await datedChecklistService.deleteItem(date, itemId);
      if (!deleteResponse.success) {
        throw new Error(deleteResponse.error || 'Failed to remove original calendar item');
      }

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: prev[date].items.filter(item => item.id !== itemId),
          itemCount: Math.max(0, (prev[date].itemCount || 1) - 1),
        },
      }));

      const destinationTitle = pasoChecklistConfigs.find(config => config.type === targetChecklistType)?.title || 'PASOS';
      setSnackbar({
        open: true,
        message: `Added to ${destinationTitle}`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to add item to PASOS',
        severity: 'error',
      });
    }
  };

  const handleColorChange = async (date: string, itemId: string, color: string) => {
    const item = checklists[date]?.items.find(i => i.id === itemId);
    const recurringMetadataId = item?.recurringMetadataId;
    
    // Optimistically update UI immediately
    if (recurringMetadataId) {
      // For recurring items, update all items with this recurringMetadataId across all dates
      setChecklists(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(checklistDate => {
          updated[checklistDate] = {
            ...updated[checklistDate],
            items: updated[checklistDate].items.map(item =>
              item.recurringMetadataId === recurringMetadataId
                ? { ...item, customColor: color }
                : item
            ),
          };
        });
        return updated;
      });
    } else {
      // For non-recurring items, just update the single item
      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          items: prev[date].items.map(item =>
            item.id === itemId ? { ...item, customColor: color } : item
          ),
        },
      }));
    }

    // Backend automatically handles recurring item updates and sync
    try {
      const response = await datedChecklistService.updateItemColor(date, itemId, color);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update color');
      }
    } catch (error) {
      // Rollback optimistic update on error
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update color',
        severity: 'error',
      });
      
      // Reload to get the correct state from server
      await loadChecklists([date]);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceDate = result.source.droppableId;
    const destDate = result.destination.droppableId;

    // Only support reordering within the same checklist for now
    if (sourceDate !== destDate) {
      setSnackbar({
        open: true,
        message: 'Moving items between dates is not supported',
        severity: 'info',
      });
      return;
    }

    // Safety check: ensure the checklist exists
    if (!checklists[sourceDate] || !checklists[sourceDate].items) {
      console.error('Checklist not found for date:', sourceDate);
      return;
    }

    const items = Array.from(checklists[sourceDate].items);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);

    // Optimistically update UI
    setChecklists(prev => ({
      ...prev,
      [sourceDate]: {
        ...prev[sourceDate],
        items,
      },
    }));

    // Calculate previousId for API
    const previousId = result.destination.index === 0 ? '' : items[result.destination.index - 1].id;

    try {
      const response = await datedChecklistService.reorderItem(sourceDate, removed.id, { previousId });
      if (!response.success) {
        throw new Error(response.error || 'Failed to reorder item');
      }
    } catch (error) {
      // Revert on error
      await loadChecklists();
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to reorder item',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleMarkReviewed = async (date: string, reviewed: boolean) => {
    try {
      const response = await datedChecklistService.markReviewed(date, reviewed);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update review status');
      }

      // Check if this was the last unreviewed date (before updating state)
      const unreviewedDates = Object.values(checklists).filter(c => !c.reviewed && c.date !== date);
      const wasLastUnreviewed = reviewed && unreviewedDates.length === 0;

      setChecklists(prev => ({
        ...prev,
        [date]: {
          ...prev[date],
          reviewed,
          reviewedAt: reviewed ? new Date().toISOString() : undefined,
        },
      }));

      // Show congratulations message if this was the last unreviewed date, otherwise show regular message
      if (wasLastUnreviewed) {
        setSnackbar({
          open: true,
          message: '🎉 Congratulations! All past dates have been reviewed!',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: reviewed ? 'Date marked as reviewed' : 'Date marked as unreviewed',
          severity: 'success',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update review status',
        severity: 'error',
      });
    }
  };

  const sortedDates = Object.keys(checklists).sort((a, b) => {
    // For future dates: sort ascending (soonest first)
    // For past dates: sort descending (most recent first)
    return filterMode === 'future' ? a.localeCompare(b) : b.localeCompare(a);
  });

  // Filter dates based on review toggle (only for past mode)
  const filteredDates = filterMode === 'past' && showOnlyUnreviewed
    ? sortedDates.filter(date => !checklists[date].reviewed)
    : sortedDates;

  // Count unreviewed dates (only for past mode)
  const unreviewedCount = filterMode === 'past'
    ? sortedDates.filter(date => !checklists[date].reviewed).length
    : 0;

  return (
    <>
      {filterMode === 'past' && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {unreviewedCount > 0 && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {unreviewedCount} date{unreviewedCount !== 1 ? 's' : ''} need{unreviewedCount === 1 ? 's' : ''} review
              </Typography>
            </Alert>
          )}
          {unreviewedCount === 0 && sortedDates.length > 0 && (
            <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                🎉 Congratulations! All past dates have been reviewed!
              </Typography>
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Show only unreviewed
            </Typography>
            <Switch
              checked={showOnlyUnreviewed}
              onChange={(e) => setShowOnlyUnreviewed(e.target.checked)}
              color="primary"
            />
          </Box>
        </Box>
      )}
      
      <ExpandableSection
        title={title}
        toggleAll={toggleAll}
        onToggleAll={handleToggleAll}
        showToggle={filterMode !== 'past'}
        showMoreButton={false}
      >
        {initialLoading ? (
          // Show skeleton loaders while initial metadata loads
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" height={64} sx={{ mb: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={64} sx={{ mb: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={64} sx={{ mb: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1 }} />
          </Box>
        ) : (
          <DragDropContext
            onDragStart={() => setIsDragInProgress(true)}
            onDragEnd={(result) => {
              setIsDragInProgress(false);
              void handleDragEnd(result);
            }}
          >
            {filteredDates.map(date => {
            const checklist = checklists[date];
            return (
              <Box
                key={date}
                ref={(el: HTMLDivElement | null) => {
                  dateRefs.current[date] = el;
                }}
              >
                <DateChecklistExpand
                  title={checklist.title}
                  dayOfWeek={checklist.dayOfWeek}
                  checklistType="want_to_do" // Reuse existing checklist type for styling
                  items={checklist.items}
                  itemCount={checklist.itemCount}
                  expanded={checklist.expanded}
                  onExpandChange={(expanded) => handleExpandChange(date, expanded)}
                  onAddItem={(text, options) => handleAddItem(date, text, options)}
                  onDeleteItem={(itemId) => handleDeleteItem(date, itemId)}
                  onEditItem={(itemId, newText, options) => handleEditItem(date, itemId, newText, undefined, false, options)}
                  onMoveItemToDate={(itemId, targetDate, newText, options) => handleMoveItemToDate(itemId, date, targetDate, newText, options)}
                  onConvertToRecurring={(itemId, recurringMetadataId) => handleEditItem(date, itemId, '', recurringMetadataId, false)}
                  onConvertToNonRecurring={(itemId, newText) => handleEditItem(date, itemId, newText, '', false)}
                  onReloadAfterRecurringUpdate={(itemId) => handleEditItem(date, itemId, '', undefined, true)}
                  onColorChange={(itemId, color) => handleColorChange(date, itemId, color)}
                  showDateField={filterMode === 'future'}
                  readOnly={filterMode === 'past'}
                  onToggleItemHidden={filterMode === 'future' ? (itemId, isHidden) => handleSetItemHidden(date, itemId, isHidden) : undefined}
                  onAddItemToPaso={filterMode === 'future' ? (itemId, targetType) => handleAddItemToPaso(date, itemId, targetType) : undefined}
                  pasoChecklistConfigs={filterMode === 'future' ? pasoChecklistConfigs : undefined}
                  isDragInProgress={isDragInProgress}
                  reviewed={filterMode === 'past' ? checklist.reviewed : undefined}
                  onMarkReviewed={filterMode === 'past' ? (reviewed) => handleMarkReviewed(date, reviewed) : undefined}
                  date={date}
                />
              </Box>
            );
          })}
        </DragDropContext>
        )}
      </ExpandableSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DateBasedChecklists;